import { z } from 'zod';
import { MongoObjectIdSchema } from '@terra/shared-utils';
import {
  DiscoverySourceSchema,
  EndpointApiTypeSchema,
} from '@terra/shared-schemas-orchestrator';
import { UnverifiedAssetStatusSchema } from './assets.schema';

export const UnverifiedAssetParamsSchema = z.object({
  applicationId: MongoObjectIdSchema.describe('The application id'),
  candidateId: MongoObjectIdSchema.describe('The endpoint candidate id'),
});

export const UnverifiedAssetOriginKindSchema = z.enum([
  'SOURCE_CODE',
  'OPENAPI',
  'JAVASCRIPT',
  'CRAWLING',
  'DIRECTORY_SCAN',
  'INTEGRATION',
  'MANUAL',
  'OTHER',
]);

export const UnverifiedAssetOriginSchema = z.object({
  kind: UnverifiedAssetOriginKindSchema,
  source: DiscoverySourceSchema,
  sourceDetail: z.string().nullable(),
});

export const ProbeTraceStepStatusSchema = z.enum([
  'OK',
  'FAILED',
  'PENDING',
  'BLOCKED',
  'INFO',
]);

export const ProbeTraceStepSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: ProbeTraceStepStatusSchema,
  statusLabel: z.string().optional(),
  summary: z.string().optional(),
  code: z.string().nullable().optional(),
  result: z.string().nullable().optional(),
});

export const ProbeTraceSchema = z.object({
  steps: ProbeTraceStepSchema.array(),
});

export const UnverifiedAssetConclusionSchema = z.object({
  summary: z.string(),
  evidence: z.string().nullable(),
});

export const UnverifiedAssetDetailSchema = z.object({
  id: z.string(),
  applicationId: z.string(),
  path: z.string(),
  host: z.string(),
  port: z.int().nullable(),
  url: z.string(),
  request: z.object({
    method: z.enum(['GET', 'POST', 'DELETE', 'PUT', 'PATCH']),
  }),
  apiType: EndpointApiTypeSchema,
  unverifiedStatus: UnverifiedAssetStatusSchema,
  origin: UnverifiedAssetOriginSchema,
  conclusion: UnverifiedAssetConclusionSchema,
  probeTrace: ProbeTraceSchema,
  failureReason: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const DeleteUnverifiedAssetResponseSchema = z.object({
  message: z.string(),
});

export const RerunUnverifiedAssetContextSchema = z.object({
  instructions: z.string().max(20_000).optional(),
  parameters: z.string().max(20_000).optional(),
  harData: z.string().max(200_000).optional(),
  usageExample: z.string().max(50_000).optional(),
});

export const RerunUnverifiedAssetBodySchema =
  RerunUnverifiedAssetContextSchema.partial();

export const RerunUnverifiedAssetResponseSchema = z.object({
  accepted: z.literal(true),
  candidateId: z.string(),
});

export const ManualProbeNameValueSchema = z.object({
  name: z.string().min(1),
  value: z.string(),
});

const ManualProbeHarNameValueSchema = z.object({
  name: z.string(),
  value: z.string(),
});

export const ManualProbeHarPostDataSchema = z
  .object({
    mimeType: z.string().optional(),
    text: z.string().optional(),
    params: z
      .array(
        z.object({
          name: z.string(),
          value: z.string().nullable().optional(),
          contentType: z.string().nullable().optional(),
        }),
      )
      .optional(),
  })
  .passthrough();

export const ManualProbeHarRequestSchema = z
  .object({
    method: z.string().optional(),
    url: z.string().optional(),
    headers: z.array(ManualProbeHarNameValueSchema).optional(),
    queryString: z.array(ManualProbeHarNameValueSchema).optional(),
    postData: ManualProbeHarPostDataSchema.optional(),
  })
  .passthrough();

export const ManualProbeHarEntrySchema = z
  .object({
    request: ManualProbeHarRequestSchema,
  })
  .passthrough();

export const ManualProbeHarLogSchema = z
  .object({
    log: z
      .object({
        entries: z.array(ManualProbeHarEntrySchema).min(1),
      })
      .passthrough(),
  })
  .passthrough();

export const ManualProbeHarPayloadSchema = z.union([
  ManualProbeHarEntrySchema,
  ManualProbeHarLogSchema,
]);

const ManualProbeParameterLineSchema = z
  .string()
  .trim()
  .transform((line, ctx) => {
    const separatorIndex = line.indexOf('=');

    if (separatorIndex <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Expected name=value',
      });

      return z.NEVER;
    }

    return {
      name: line.slice(0, separatorIndex).trim(),
      value: line.slice(separatorIndex + 1).trim(),
    };
  })
  .pipe(ManualProbeNameValueSchema);

export const ManualProbeBypassSchema = z.object({
  bypassHumanVerification: z.boolean(),
  bypassExecutionPolicy: z.boolean(),
});

export const EndpointCandidateManualProbeMetadataSchema = z
  .object({
    manualProbeContext: RerunUnverifiedAssetContextSchema.partial().optional(),
    manualProbeBypass: ManualProbeBypassSchema.partial().optional(),
  })
  .passthrough();

export const parseManualProbeParametersText = (
  parameters: string,
): ManualProbeNameValue[] =>
  parameters
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .flatMap((line) => {
      const parsed = ManualProbeParameterLineSchema.safeParse(line);

      return parsed.success ? [parsed.data] : [];
    });

export const parseManualProbeHarData = (
  harData: string,
): {
  headers?: ManualProbeNameValue[];
  queryString?: ManualProbeNameValue[];
  postData?: {
    mimeType: string | null;
    text: string | null;
    params: {
      name: string;
      value: string | null;
      contentType: string | null;
    }[];
  } | null;
} | null => {
  try {
    const parsedJson = JSON.parse(harData) as z.infer<
      typeof ManualProbeHarPayloadSchema
    >;
    const entryResult = ManualProbeHarEntrySchema.safeParse(parsedJson);
    const logResult = entryResult.success
      ? null
      : ManualProbeHarLogSchema.safeParse(parsedJson);
    const request = entryResult.success
      ? entryResult.data.request
      : logResult?.success
        ? logResult.data.log.entries[0]?.request
        : undefined;

    if (!request) {
      return null;
    }

    const headers = request.headers
      ?.filter((header) => header.name.trim().length > 0)
      .map((header) => ({
        name: header.name,
        value: header.value,
      }));
    const queryString = request.queryString
      ?.filter((query) => query.name.trim().length > 0)
      .map((query) => ({
        name: query.name,
        value: query.value,
      }));

    return {
      headers,
      queryString,
      postData: request.postData
        ? {
            mimeType: request.postData.mimeType ?? null,
            text: request.postData.text ?? null,
            params:
              request.postData.params?.map((param) => ({
                name: param.name,
                value: param.value ?? null,
                contentType: param.contentType ?? null,
              })) ?? [],
          }
        : null,
    };
  } catch {
    return null;
  }
};

export const UnverifiedAssetProbeStatusSchema = z.enum([
  ...UnverifiedAssetStatusSchema.options,
  'VERIFIED',
  'NOT_FOUND',
]);

export const UnverifiedAssetProbeStatusResponseSchema = z.object({
  candidateId: z.string(),
  status: UnverifiedAssetProbeStatusSchema,
  endpointId: z.string().nullable(),
  webpageId: z.string().nullable(),
});

export type UnverifiedAssetOriginKind = z.infer<
  typeof UnverifiedAssetOriginKindSchema
>;
export type ProbeTraceStepStatus = z.infer<typeof ProbeTraceStepStatusSchema>;
export type ProbeTraceStep = z.infer<typeof ProbeTraceStepSchema>;
export type ProbeTrace = z.infer<typeof ProbeTraceSchema>;
export type UnverifiedAssetOrigin = z.infer<typeof UnverifiedAssetOriginSchema>;
export type UnverifiedAssetConclusion = z.infer<
  typeof UnverifiedAssetConclusionSchema
>;
export type UnverifiedAssetDetail = z.infer<typeof UnverifiedAssetDetailSchema>;
export type RerunUnverifiedAssetContext = z.infer<
  typeof RerunUnverifiedAssetContextSchema
>;
export type RerunUnverifiedAssetBody = z.infer<
  typeof RerunUnverifiedAssetBodySchema
>;
export type RerunUnverifiedAssetResponse = z.infer<
  typeof RerunUnverifiedAssetResponseSchema
>;
export type ManualProbeNameValue = z.infer<typeof ManualProbeNameValueSchema>;
export type ManualProbeBypass = z.infer<typeof ManualProbeBypassSchema>;
export type EndpointCandidateManualProbeMetadata = z.infer<
  typeof EndpointCandidateManualProbeMetadataSchema
>;
export type UnverifiedAssetProbeStatus = z.infer<
  typeof UnverifiedAssetProbeStatusSchema
>;
export type UnverifiedAssetProbeStatusResponse = z.infer<
  typeof UnverifiedAssetProbeStatusResponseSchema
>;
